/**
 * 时间渲染单一真源(时区统一,见 docs/todo/20)
 *
 * 铁律:库里一律存**真 UTC 瞬时**;**所有面向人的时间渲染只走本文件**,钉死北京时区
 * (`Asia/Shanghai`),不靠浏览器本地、不裸 `getHours()/getUTCHours()`。
 * 给本地数字贴 `Z` 当 UTC = 禁止(那是历史 bug 根源)。
 */

const BJ = "Asia/Shanghai"
const pad2 = (value: number) => String(value).padStart(2, "0")

export type BjDateParts = {
  year: number
  month: number
  day: number
}

// 用 Intl 在北京时区拆出 年/月/日/时/分/秒(h23 避免午夜 "24" 边界)
function bjParts(iso: string): Record<string, string> {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: BJ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  })
  const out: Record<string, string> = {}
  for (const p of dtf.formatToParts(new Date(iso))) out[p.type] = p.value
  return out
}

export type FmtMode = "datetime" | "date" | "time"

/** 北京时区渲染:datetime=YYYY-MM-DD HH:mm / date=YYYY-MM-DD / time=HH:mm */
export function fmtBJ(iso: string, mode: FmtMode = "datetime"): string {
  const p = bjParts(iso)
  const date = `${p.year}-${p.month}-${p.day}`
  const time = `${p.hour}:${p.minute}`
  if (mode === "date") return date
  if (mode === "time") return time
  return `${date} ${time}`
}

/** 北京时区的小时(0-23),给"进单时段"统计用 */
export function bjHour(iso: string): number {
  return Number(bjParts(iso).hour)
}

/** 当前北京时间自然日拆分,所有“今天 / 本月 / 当前年”判断都应从这里派生。 */
export function bjTodayParts(): BjDateParts {
  const p = bjParts(new Date().toISOString())
  return {
    year: Number(p.year),
    month: Number(p.month),
    day: Number(p.day),
  }
}

/** 当前北京时间自然日，供只能面向今天及未来的业务校验使用。 */
export function bjToday(): string {
  const p = bjTodayParts()
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}`
}

/** 当前北京时间年份。 */
export function bjCurrentYear(): number {
  return bjTodayParts().year
}

/** 当前北京时间月份，YYYY-MM。 */
export function bjCurrentMonth(): string {
  const p = bjTodayParts()
  return `${p.year}-${pad2(p.month)}`
}

/** 上一北京时间自然月，YYYY-MM。 */
export function bjPrevMonth(): string {
  const p = bjTodayParts()
  const year = p.month === 1 ? p.year - 1 : p.year
  const month = p.month === 1 ? 12 : p.month - 1
  return `${year}-${pad2(month)}`
}

/** 昨天的北京时间自然日，YYYY-MM-DD。 */
export function bjYesterday(): string {
  const today = bjToday()
  const start = new Date(`${today}T00:00:00+08:00`)
  return fmtBJ(new Date(start.getTime() - 86_400_000).toISOString(), "date")
}
